import { Component, OnInit } from '@angular/core';
import { BackendService } from '../../services/backend.service';
import { AuthService } from '../../services/auth.service';
import { Detection, Facility } from '../../types/common.types';
import { formatDate } from '@angular/common';
import { NotificationService } from '../../services/notification.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';

@Component({
  selector: 'app-count',
  templateUrl: './count.component.html',
  styleUrl: './count.component.css'
})

export class CountComponent implements OnInit {

  facilities: Facility[] = [];
  detections: Detection[] = [];

  selectedDetection: Detection | undefined;
  selectedFacility: Facility | undefined;
  selectedTime: Date | undefined;
  isLoading = false;

  // Date-related
  currentYear: number;
  currentMonth: number;
  daysInMonth: number[] = [];
  selectedDay: number;

  monthNames: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Modal logic
  showModal = false; // Whether the modal is shown
  count: number | string | null = null;  // Temporary input for persons
  selectedTimeSlotIndex: number | null = null; // Which slot is being edited
  selectedTimeSlotLabel = '';     // For the modal header

  constructor(
    private backendService: BackendService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router
  ) {
    const today = new Date();
    this.currentYear = today.getFullYear();
    this.currentMonth = today.getMonth();
    this.selectedDay = today.getDate();
  }

  logout(): void {
    this.authService.logout();
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.backendService.facilities().pipe(
      catchError(() => {
        // Error is already handled by the interceptor
        return of([]);
      }),
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe((valid) => {
      this.facilities = valid;
      this.generateDaysInMonth();
      this.selectDay(this.selectedDay);
    });
  }

  generateDaysInMonth(): void {
    const lastDayOfCurrentMonth = new Date(this.currentYear, this.currentMonth + 1, 0);
    const numDays = lastDayOfCurrentMonth.getDate();
    this.daysInMonth = Array.from({ length: numDays }, (_, i) => i + 1);
  }

  prevMonth(): void {
    this.currentMonth--;
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    }
    this.generateDaysInMonth();
  }

  nextMonth(): void {
    this.currentMonth++;
    if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    this.generateDaysInMonth();
  }

  selectDay(day: number): void {
    this.selectedDay = day;
    const selecctedDate = new Date(this.currentYear, this.currentMonth, this.selectedDay);
    if(this.selectedFacility) {
      this.isLoading = true;
      this.backendService.detections(this.selectedFacility.id, selecctedDate, selecctedDate, 1, 'days').pipe(
        catchError(() => {
          // Error is already handled by the interceptor
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      ).subscribe((valid) => {
        this.detections = valid;
      });
    }
  }

  onFacilityChange(event: Event): void {
      const selectElement = event.target as HTMLSelectElement;
      const newValue = selectElement.value;

      this.selectedFacility = this.facilities.find(x => x.id == Number(newValue));
      this.selectDay(this.selectedDay);
  }

  openModal(detection: Detection) {
    this.selectedDetection = detection;
    const startTime = formatDate(new Date( this.selectedDetection.startTime), 'HH:mm', 'en-DK');
    const endTime = formatDate(new Date( this.selectedDetection.endTime), 'HH:mm', 'en-DK');
    this.selectedTimeSlotLabel = `${startTime} - ${endTime}`;

    if(this.selectedDetection.count) {
      this.count = this.selectedDetection.count;
    }
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.selectedTimeSlotIndex = null;
    this.count = 0;
    this.selectedTimeSlotLabel = '';
  }

  // Save the persons value to the time slot array
  savePersons() {
    if(this.selectedDetection) {
      const dbtime = new Date(this.selectedDetection.startTime);
      dbtime.setMinutes(30);

      if(this.selectedFacility) {
        this.isLoading = true;
        if(this.selectedDetection.manualDetectionDtos.length == 0) {
          this.backendService.createDetection(
            Number(this.count),
            this.selectedFacility?.organization_id,
            this.selectedFacility?.department_id,
            this.selectedFacility?.id,
            dbtime
          ).pipe(
            catchError(() => {
              // Error is already handled by the interceptor
              return of(null);
            }),
            finalize(() => {
              this.isLoading = false;
            })
          ).subscribe((response) => {
            if (response) {
              this.notificationService.showSuccess('Count saved successfully');
              this.selectDay(this.selectedDay);
            }
            this.closeModal();
          });
        } else {
          if(this.selectedDetection.manualDetectionDtos[0].id) {
            this.backendService.setDetections(
              this.selectedDetection.manualDetectionDtos[0].id,
              this.selectedFacility?.organization_id,
              this.selectedFacility?.department_id,
              this.selectedFacility?.id,
              Number(this.count),
              dbtime
            ).pipe(
              catchError(() => {
                // Error is already handled by the interceptor
                return of(null);
              }),
              finalize(() => {
                this.isLoading = false;
              })
            ).subscribe((response) => {
              if (response) {
                this.notificationService.showSuccess('Count updated successfully');
                this.selectDay(this.selectedDay);
              }
              this.closeModal();
            });
          }
        }
      }
    } else {
      this.closeModal();
    }
  }

  goToDateFacilityFlow() {
    this.router.navigate(['/date-facility-flow']);
  }
}
